import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { appInnerCardStyle, appMutedTextStyle, appNeutralPanelStyle, renderSurfaceHeader } from "../../../components/general/uiSurfaceStyles";
import { getErrorTitle } from "../../../constants/constants";
import { ROLE_TYPE_CAREGIVER } from "../../../constants/RegisterConstants";
import ProviderSubscriptionDialog from "../../../components/general/ProviderSubscriptionDialog";
import Unauthorized from "../../../shared/pages/Unauthorized";
import ResidentEmarTodayPanel from "../../resident/components/ResidentEmarTodayPanel";
import {
  buildEmarDailyTrackingValueSummary,
  getEmarDailyTrackingCategoryOption,
  getEmarDailyTrackingFieldPlaceholder,
  getEmarDailyTrackingSchema,
} from "../../resident/utils/emarDailyTrackingUtils";
import {
  buildMedicationScheduleText,
  getMedicationScheduleDisplay,
  getMedicationScheduleOccurrences,
  getTodayMedicationTimingMeta,
} from "../../resident/utils/emarScheduleUtils";
import { getEmarProtocolSummary } from "../../resident/utils/emarProtocolUtils";
import { ensureCaregiverResidentOperationalAccess } from "../utils/caregiverResidentAccess";
import { canAccessCaregiverRole } from "../utils/caregiverRoleAccess";

const getEmarEntryOccurrenceKey = (entry: any) => (
  `${String(entry?.medicationId || "").trim()}::${(() => {
    const scheduledTimeKey = String(entry?.scheduledTimeKey || (String(entry?.status || "").toLowerCase() === "prn_given" ? "prn" : "default")).trim().toLowerCase();
    return scheduledTimeKey.startsWith("prn") ? "prn" : scheduledTimeKey;
  })()}`
);

const getIsoDateOnly = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const LOS_ANGELES_DATE_ONLY_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const getLosAngelesDateOnly = (value: Date = new Date()) => LOS_ANGELES_DATE_ONLY_FORMATTER.format(value);

const getMonthStartDateOnly = (value: Date) => (
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-01`
);

const getInclusiveDateRangeDayCount = (startDate: string, endDate: string) => {
  const parseDateKey = (value: string) => {
    const [year, month, day] = String(value || "").trim().split("-").map(Number);
    if (!year || !month || !day) {
      return Number.NaN;
    }
    return Date.UTC(year, month - 1, day);
  };
  const start = parseDateKey(startDate);
  const end = parseDateKey(endDate);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return 0;
  }
  return Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1;
};

const reportRangeButtonStyle: React.CSSProperties = {
  borderRadius: "999px",
  border: "1px solid #cbd5e1",
  padding: "0.35rem 0.65rem",
  fontSize: "0.78rem",
  fontWeight: 700,
  whiteSpace: "nowrap",
  boxShadow: "none",
};

const getEntryRecordedLocalDate = (entry: any) => {
  const timestamp = String(entry?.recordedAt || entry?.modifiedAt || entry?.createdAt || "").trim();
  if (!timestamp) {
    return "";
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  return getIsoDateOnly(parsedDate);
};

const filterEntriesForTodayPass = (entries: any[], todayDate: string) => (
  (Array.isArray(entries) ? entries : []).filter((entry: any) => {
    const recordedLocalDate = getEntryRecordedLocalDate(entry);
    return !recordedLocalDate || recordedLocalDate === todayDate;
  })
);

const getDailyTrackingOccurrenceKey = (entry: any) => (
  `${String(entry?.trackingItemId || "").trim()}::${String(entry?.scheduledTimeKey || "default").trim().toLowerCase()}`
);

const filterEmarPassRows = (rows: any[], passFilter: "all" | "pending" | "charted") => (
  (Array.isArray(rows) ? rows : [])
    .map((row: any) => ({
      ...row,
      occurrences: (Array.isArray(row?.occurrences) ? row.occurrences : []).filter((occurrence: any) => {
        if (passFilter === "all") {
          return true;
        }
        const isCharted = Boolean(occurrence?.entry && String(occurrence?.entryStatus || "").trim());
        return passFilter === "charted" ? isCharted : !isCharted;
      }),
    }))
    .filter((row: any) => row.occurrences.length > 0)
);

const normalizeValueKey = (value: any) => String(value || "").trim().toLowerCase().replaceAll(" ", "_");

const formatDailyTrackingStatusLabel = (value: any) => {
  const normalized = normalizeValueKey(value);
  if (normalized === "missed") {
    return "Missed";
  }
  if (normalized === "recorded") {
    return "Recorded";
  }
  if (normalized === "refused") {
    return "Refused";
  }
  if (normalized === "family") {
    return "Family";
  }
  if (normalized === "hospitalized") {
    return "Hospitalized";
  }
  return "Pending";
};

const getTrackingRecommendedActionLabel = (entryLike: any) => {
  const normalizedActionLabel = String(
    entryLike?.protocolAssessment?.resolution?.recommendedActionLabel
    || entryLike?.protocolAssessment?.resolution?.recommendedAction
    || "",
  ).trim();
  if (!normalizedActionLabel) {
    return "";
  }
  if (normalizedActionLabel.includes("_")) {
    return normalizedActionLabel
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }
  return normalizedActionLabel;
};

const getTrackingRecommendedActionMessage = (entryLike: any) => {
  const actionLabel = getTrackingRecommendedActionLabel(entryLike);
  if (!actionLabel) {
    return "";
  }
  const decisionNote = String(
    entryLike?.protocolAssessment?.resolution?.decisionNote
    || entryLike?.protocolAssessment?.decisionNote
    || "",
  ).trim();
  return decisionNote
    ? `Based on the reading and rules, please ${actionLabel.toLowerCase()}. ${decisionNote}`
    : `Based on the reading and rules, please ${actionLabel.toLowerCase()}.`;
};

const getDailyTrackingStatusSeverity = (value: any) => {
  const normalized = normalizeValueKey(value);
  if (normalized === "missed") {
    return "danger";
  }
  if (normalized === "recorded" || normalized === "family") {
    return "success";
  }
  if (normalized === "refused") {
    return "danger";
  }
  if (normalized === "hospitalized") {
    return "warning";
  }
  return "secondary";
};

const buildOccurrenceSummaryTag = (
  occurrence: any,
  statusLabel?: string,
  statusSeverity?: string,
) => {
  const timingMeta = occurrence?.timingMeta || {};
  const timeLabel = String(occurrence?.occurrenceLabel || timingMeta?.scheduledLabel || "Scheduled").trim() || "Scheduled";
  const isPrnOccurrence = String(timeLabel).trim().toUpperCase() === "PRN";
  const suffix = timeLabel && timeLabel !== "Scheduled" ? ` ${timeLabel}` : "";

  if (occurrence?.rawEntryStatus === "prn_given" && !String(occurrence?.entry?.prnReassessedAt || "").trim()) {
    return {
      label: `Follow-up Pending${suffix}`,
      severity: "warning",
    };
  }
  if (statusLabel) {
    return {
      label: `${statusLabel}${suffix}`,
      severity: statusSeverity || "secondary",
    };
  }
  if (timingMeta?.isOverdue) {
    return {
      label: `Missed${suffix}`,
      severity: "danger",
    };
  }
  if (timingMeta?.isUpcoming) {
    return {
      label: `Upcoming${suffix}`,
      severity: "secondary",
    };
  }
  if (isPrnOccurrence) {
    return null;
  }
  return {
    label: `Pending${suffix}`,
    severity: timingMeta?.isDueSoon ? "info" : "secondary",
  };
};

const isDailyTrackingValueEntryStatus = (value: any) => {
  const normalized = normalizeValueKey(value);
  return normalized === "recorded";
};

const isMarOnlyMedication = (item: any) => (
  item?.marOnlyItem === true
  || String(item?.medicationType || "").trim().toLowerCase() === "mar_only"
  || String(item?.medicationCategory || "").trim().toLowerCase() === "mar_only"
);

const formatEmarDateOnly = (value: any) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return "";
  }
  const dateOnlyMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const parsedDate = new Date(
      Number(dateOnlyMatch[1]),
      Number(dateOnlyMatch[2]) - 1,
      Number(dateOnlyMatch[3]),
      12,
      0,
      0,
      0,
    );
    return Number.isNaN(parsedDate.getTime()) ? normalized : parsedDate.toLocaleDateString();
  }
  const parsedDate = new Date(normalized);
  if (Number.isNaN(parsedDate.getTime())) {
    return normalized;
  }
  return parsedDate.toLocaleDateString();
};

const getLocalTimeValueWithSecondsFromIso = (value: any) => {
  const timestamp = String(value || "").trim();
  if (!timestamp) {
    return "";
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  const hours = String(parsedDate.getHours()).padStart(2, "0");
  const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
  const seconds = String(parsedDate.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

const updateIsoDateTimeParts = (sourceValue: any, nextDate?: string, nextTime?: string) => {
  const baseDate = String(sourceValue || "").trim() ? new Date(String(sourceValue || "").trim()) : new Date();
  if (Number.isNaN(baseDate.getTime())) {
    return "";
  }
  const dateValue = String(nextDate || getIsoDateOnly(baseDate)).trim();
  const timeValue = String(nextTime || getLocalTimeValueWithSecondsFromIso(baseDate).slice(0, 5)).trim() || "00:00";
  const [year, month, day] = dateValue.split("-").map((part) => Number(part));
  const [hours, minutes, seconds] = timeValue.split(":").map((part) => Number(part));
  if (!year || !month || !day) {
    return "";
  }
  const nextRecordedAt = new Date(year, month - 1, day, Number.isFinite(hours) ? hours : 0, Number.isFinite(minutes) ? minutes : 0, Number.isFinite(seconds) ? seconds : 0, 0);
  return Number.isNaN(nextRecordedAt.getTime()) ? "" : nextRecordedAt.toISOString();
};

const formatTimeChoiceLabel = (value: any) => {
  const timestamp = String(value || "").trim();
  if (!timestamp) {
    return "";
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  return parsedDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });
};

const SCHEDULE_TIME_OPTIONS = Array.from({ length: 24 * 4 }, (_, index) => {
  const hours = Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return {
    value,
    label: date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
  };
});

const getAvailableChartTimeOptions = (entryDate?: string) => {
  const normalizedEntryDate = String(entryDate || "").trim();
  if (!normalizedEntryDate || normalizedEntryDate !== getIsoDateOnly(new Date())) {
    return SCHEDULE_TIME_OPTIONS;
  }
  const currentTimeValue = `${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`;
  return SCHEDULE_TIME_OPTIONS.filter((option) => String(option?.value || "") <= currentTimeValue);
};

const buildDailyTrackingTimeSelectionState = (entryDate: string, scheduledClockTime: string, baseTimestamp: string, includePromptReason = true) => {
  const now = new Date();
  const currentTimeValue = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
  const currentRecordedAt = updateIsoDateTimeParts(baseTimestamp || now.toISOString(), entryDate, currentTimeValue);
  const scheduledRecordedAt = scheduledClockTime
    ? updateIsoDateTimeParts(baseTimestamp || now.toISOString(), entryDate, scheduledClockTime)
    : currentRecordedAt;
  const scheduledOffsetMinutes = scheduledClockTime
    ? Math.round((new Date(currentRecordedAt).getTime() - new Date(scheduledRecordedAt).getTime()) / 60000)
    : null;
  const shouldShowPrompt = !!includePromptReason && !!scheduledClockTime && scheduledOffsetMinutes !== null && Math.abs(scheduledOffsetMinutes) > 60;
  const timingDirection = scheduledOffsetMinutes !== null && scheduledOffsetMinutes < 0 ? "before" : "after";
  const roundedHours = scheduledOffsetMinutes === null ? "" : (Math.abs(scheduledOffsetMinutes) / 60).toFixed(1).replace(/\\.0$/, "");
  return {
    currentRecordedAt,
    scheduledRecordedAt,
    givenTimeChoice: scheduledClockTime ? "scheduled" : "current",
    showGivenTimeChoiceDialog: shouldShowPrompt,
    givenTimePromptReason: shouldShowPrompt
      ? `This item is being charted about ${roundedHours} hour${roundedHours === "1" ? "" : "s"} ${timingDirection} the scheduled time. Choose whether to save it at the scheduled time or the current time.`
      : "",
    givenTimeOptions: [
      {
        value: "scheduled",
        label: "Given at scheduled time",
        disabled: !scheduledClockTime,
        helper: !scheduledClockTime ? "No scheduled clock time set" : formatTimeChoiceLabel(scheduledRecordedAt),
        recordedAt: scheduledRecordedAt,
      },
      {
        value: "current",
        label: "Given at current time",
        helper: formatTimeChoiceLabel(currentRecordedAt),
        recordedAt: currentRecordedAt,
      },
      {
        value: "custom",
        label: "Exact time",
        helper: "Choose a specific charting time below.",
        recordedAt: currentRecordedAt,
      },
    ],
  };
};

const getScheduledChartUnlockTime = (scheduledDate?: Date | null) => {
  if (!(scheduledDate instanceof Date) || Number.isNaN(scheduledDate.getTime())) {
    return null;
  }
  return new Date(scheduledDate.getTime() - (60 * 60 * 1000));
};

const getCurrentDateOnlyLabel = () => (
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date())
);

const resolveOccurrenceEntryDate = (occurrence: any) => (
  String(occurrence?.entryDate || occurrence?.timingMeta?.entryDate || "").trim()
);

const resolveOccurrenceChartDate = (occurrence: any) => {
  const explicitEntryDate = resolveOccurrenceEntryDate(occurrence);
  if (explicitEntryDate) {
    return explicitEntryDate;
  }
  const scheduledDate = occurrence?.timingMeta?.scheduledDate;
  if (!(scheduledDate instanceof Date) || Number.isNaN(scheduledDate.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(scheduledDate);
};

const canChartScheduledOccurrenceNow = (occurrence: any) => {
  const occurrenceEntryDate = resolveOccurrenceChartDate(occurrence);
  const currentDateOnly = getCurrentDateOnlyLabel();
  if (!occurrenceEntryDate || occurrenceEntryDate < currentDateOnly) {
    return true;
  }
  if (occurrenceEntryDate > currentDateOnly) {
    return false;
  }
  const unlockTime = getScheduledChartUnlockTime(occurrence?.timingMeta?.scheduledDate);
  if (!unlockTime) {
    return true;
  }
  return Date.now() >= unlockTime.getTime();
};

const formatChartUnlockTimeLabel = (scheduledDate?: Date | null) => {
  const unlockTime = getScheduledChartUnlockTime(scheduledDate);
  if (!unlockTime) {
    return "";
  }
  return unlockTime.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const parseMedicationExpiryValue = (value: any) => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue || !/^\d{4}-\d{2}-\d{2}$/.test(normalizedValue)) {
    return null;
  }
  const parsedExpiry = new Date(`${normalizedValue}T00:00:00`);
  return Number.isNaN(parsedExpiry.getTime()) ? null : parsedExpiry;
};

const isMedicationExpiringWithinMonth = (value: any) => {
  const parsedExpiry = parseMedicationExpiryValue(value);
  if (!parsedExpiry) {
    return false;
  }
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const threshold = new Date(now);
  threshold.setDate(threshold.getDate() + 30);
  return parsedExpiry.getTime() >= now.getTime() && parsedExpiry.getTime() <= threshold.getTime();
};

const isMedicationExpired = (value: any) => {
  const parsedExpiry = parseMedicationExpiryValue(value);
  if (!parsedExpiry) {
    return false;
  }
  return parsedExpiry.getTime() < Date.now();
};


type CaregiverResidentEmarPageProps = {
  sharedMode?: boolean;
};

export const CaregiverResidentEmarPageContent = ({ sharedMode = false }: CaregiverResidentEmarPageProps) => {
  const { residentId } = useParams();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);

  const userRoleType = authStore.getUserRoleType();
  const isAdminUser = authStore.isAdmin();
  const canUseCaregiverWorkspace = sharedMode
    ? Boolean(userRoleType) || isAdminUser
    : canAccessCaregiverRole(authStore);
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showGenerateReportDialog, setShowGenerateReportDialog] = useState(false);
  const [reportRangeMode, setReportRangeMode] = useState<"monthly" | "exact_dates">("monthly");
  const [reportMonthSelection, setReportMonthSelection] = useState<Date>(new Date());
  const [reportStartDateSelection, setReportStartDateSelection] = useState(getMonthStartDateOnly(new Date()));
  const [reportEndDateSelection, setReportEndDateSelection] = useState(getLosAngelesDateOnly());
  const [reportExactDateSelection, setReportExactDateSelection] = useState<(Date | null)[]>([new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date()]);
  const [blankMarReport, setBlankMarReport] = useState(false);
  const [savingEntry, setSavingEntry] = useState(false);
  const [savingEntryKey, setSavingEntryKey] = useState("");
  const [residentAccess, setResidentAccess] = useState<any>(null);
  const [medications, setMedications] = useState<any[]>([]);
  const [todayEntries, setTodayEntries] = useState<any[]>([]);
  const [dailyTrackingItems, setDailyTrackingItems] = useState<any[]>([]);
  const [todayTrackingEntries, setTodayTrackingEntries] = useState<any[]>([]);
  const [savingDailyTrackingKey, setSavingDailyTrackingKey] = useState("");
  const [showDailyTrackingDialog, setShowDailyTrackingDialog] = useState(false);
  const [pendingDailyTrackingAction, setPendingDailyTrackingAction] = useState<any>(null);
  const [trackingRecommendedActionDialog, setTrackingRecommendedActionDialog] = useState<{ title: string; message: string } | null>(null);
  const [dailyTrackingValueDraft, setDailyTrackingValueDraft] = useState<Record<string, string>>({});
  const [dailyTrackingNoteDraft, setDailyTrackingNoteDraft] = useState("");
  const [accessMessage, setAccessMessage] = useState("");
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [todayPassViewMode, setTodayPassViewMode] = useState<"medication" | "time">("medication");
  const [todayPassFilter, setTodayPassFilter] = useState<"all" | "pending" | "charted">("all");
  const [showMedicationExpiryAlertsExpanded, setShowMedicationExpiryAlertsExpanded] = useState(false);
  const todayDate = getLosAngelesDateOnly();
  const monthStartDate = getMonthStartDateOnly(new Date());

  const activeMedications = useMemo(
    () => medications.filter((item: any) => item?.active !== false && item?.discontinued !== true && !isMarOnlyMedication(item)),
    [medications],
  );
  const activeNonPrnMedications = useMemo(
    () => activeMedications.filter((item: any) => item?.prn !== true),
    [activeMedications],
  );
  const activePrnMedications = useMemo(
    () => activeMedications.filter((item: any) => item?.prn === true),
    [activeMedications],
  );
  const activeNonPrnMedicationsForMedPass = useMemo(
    () => activeNonPrnMedications.filter((item: any) => !isMedicationExpired(item?.effectiveEndDate)),
    [activeNonPrnMedications],
  );
  const activePrnMedicationsForMedPass = useMemo(
    () => activePrnMedications.filter((item: any) => !isMedicationExpired(item?.effectiveEndDate)),
    [activePrnMedications],
  );
  const activeDailyTrackingItems = useMemo(
    () => dailyTrackingItems.filter((item: any) => item?.active !== false && item?.discontinued !== true),
    [dailyTrackingItems],
  );
  const activeNonPrnMedicationsScheduledToday = useMemo(
    () => activeNonPrnMedicationsForMedPass.filter((medication: any) => getMedicationScheduleOccurrences(medication, todayDate).length > 0),
    [activeNonPrnMedicationsForMedPass, todayDate],
  );
  const residentDisplayName = useMemo(
    () => String(residentAccess?.residentName || "").trim() || `Resident ${residentId || ""}`,
    [residentAccess?.residentName, residentId],
  );
  const todayScheduledMedicationIds = useMemo(
    () => new Set(activeNonPrnMedicationsScheduledToday.map((medication: any) => String(medication?.medicationId || "").trim()).filter(Boolean)),
    [activeNonPrnMedicationsScheduledToday],
  );
  const prnMedicationIds = useMemo(
    () => new Set(activePrnMedications.map((medication: any) => String(medication?.medicationId || "").trim()).filter(Boolean)),
    [activePrnMedications],
  );
  const scheduledTodayEntries = useMemo(
    () => todayEntries.filter((entry: any) => todayScheduledMedicationIds.has(String(entry?.medicationId || "").trim())),
    [todayEntries, todayScheduledMedicationIds],
  );
  const prnTodayEntries = useMemo(
    () => todayEntries.filter((entry: any) => prnMedicationIds.has(String(entry?.medicationId || "").trim())),
    [todayEntries, prnMedicationIds],
  );
  const todayDailyTrackingEntryByOccurrenceKey = useMemo(
    () => Object.fromEntries(todayTrackingEntries.map((entry: any) => [getDailyTrackingOccurrenceKey(entry), entry])),
    [todayTrackingEntries],
  );
  const dailyTrackingRows = useMemo(() => (
    activeDailyTrackingItems
      .map((item: any) => {
        const trackingItemId = String(item?.trackingItemId || "").trim();
        const occurrences = getMedicationScheduleOccurrences(item, todayDate).map((occurrence: any) => {
          const scheduledTimeKey = String(occurrence?.scheduledTimeKey || "default").trim().toLowerCase();
          const entry = todayDailyTrackingEntryByOccurrenceKey[`${trackingItemId}::${scheduledTimeKey}`];
          return {
            trackingItemId,
            entry,
            entryStatus: String(entry?.status || "").trim().toLowerCase(),
            scheduledTimeKey: occurrence?.scheduledTimeKey || "default",
            occurrenceLabel: occurrence?.scheduledLabel || "",
            timingMeta: getTodayMedicationTimingMeta({
              schedule: occurrence?.scheduleText || item?.schedule || "",
              scheduledClockTime: occurrence?.scheduledClockTime || "",
              prn: false,
            }, !!entry),
          };
        });
        return { item, trackingItemId, occurrences };
      })
      .filter((row: any) => row.occurrences.length > 0)
      .sort((first: any, second: any) => String(first?.item?.name || "").localeCompare(String(second?.item?.name || "")))
  ), [activeDailyTrackingItems, todayDate, todayDailyTrackingEntryByOccurrenceKey]);
  const filteredDailyTrackingRows = useMemo(
    () => filterEmarPassRows(dailyTrackingRows, todayPassFilter),
    [dailyTrackingRows, todayPassFilter],
  );
  const todayPassSummary = useMemo(() => ({
    updated: scheduledTodayEntries.length,
    pending: Math.max(
      activeNonPrnMedicationsScheduledToday.reduce((total: number, medication: any) => total + getMedicationScheduleOccurrences(medication, todayDate).length, 0) - scheduledTodayEntries.length,
      0,
    ),
    unscheduledNonPrn: activeNonPrnMedicationsScheduledToday.filter((medication: any) => getMedicationScheduleOccurrences(medication, todayDate).some((occurrence: any) => String(occurrence?.scheduledLabel || "").trim().toLowerCase() === "unscheduled")).length,
    missedScheduled: activeNonPrnMedicationsScheduledToday.reduce((total: number, medication: any) => (
      total + getMedicationScheduleOccurrences(medication, todayDate).filter((occurrence: any) => {
        const medicationId = String(medication?.medicationId || "").trim();
        const occurrenceKey = `${medicationId}::${String(occurrence?.scheduledTimeKey || "default").trim().toLowerCase()}`;
        const entry = todayEntries.find((item: any) => getEmarEntryOccurrenceKey(item) === occurrenceKey);
        if (entry) {
          return false;
        }
        const timingMeta = getTodayMedicationTimingMeta({
          schedule: occurrence?.scheduleText || medication?.schedule || "",
          scheduledClockTime: occurrence?.scheduledClockTime || "",
          prn: false,
        }, false);
        return timingMeta?.isOverdue === true;
      }).length
    ), 0),
    missedEntries: scheduledTodayEntries.filter((entry: any) => String(entry?.status || "").toLowerCase() === "missed").length,
    missedTracking: dailyTrackingRows.reduce((total: number, row: any) => (
      total + row.occurrences.filter((occurrence: any) => {
        const status = String(occurrence?.entryStatus || "").trim().toLowerCase();
        return status === "missed" || (!status && occurrence?.timingMeta?.isOverdue);
      }).length
    ), 0),
    expired: activeNonPrnMedications.filter((medication: any) => isMedicationExpired(medication?.effectiveEndDate)).length,
    expiringSoon: activeNonPrnMedicationsScheduledToday.filter((medication: any) => (
      !isMedicationExpired(medication?.effectiveEndDate)
      && isMedicationExpiringWithinMonth(medication?.effectiveEndDate)
    )).length,
  }), [scheduledTodayEntries, activeNonPrnMedications, activeNonPrnMedicationsScheduledToday, todayDate, todayEntries, dailyTrackingRows]);
  const medicationExpiryAlerts = useMemo(() => (
    activeMedications
      .filter((item: any) => item?.effectiveEndDate && (
        isMedicationExpired(item.effectiveEndDate) || isMedicationExpiringWithinMonth(item.effectiveEndDate)
      ))
      .map((item: any) => ({
        medicationId: item?.medicationId || item?.residentEmarMedicationId || item?.id || item?.name,
        medicationName: String(item?.name || item?.medicationName || item?.medicineName || item?.drugName || item?.label || "").trim() || "Medication",
        effectiveEndDate: item?.effectiveEndDate,
        status: isMedicationExpired(item?.effectiveEndDate) ? "expired" : "expiring",
      }))
      .sort((left, right) => {
        if (left.status !== right.status) {
          return left.status === "expired" ? -1 : 1;
        }
        const leftTime = Date.parse(String(left.effectiveEndDate || "")) || 0;
        const rightTime = Date.parse(String(right.effectiveEndDate || "")) || 0;
        return leftTime - rightTime;
      })
  ), [activeMedications]);
  const prnFollowUpPendingCount = useMemo(
    () => prnTodayEntries.filter((entry: any) => String(entry?.status || "").toLowerCase() === "prn_given" && !String(entry?.prnReassessedAt || "").trim()).length,
    [prnTodayEntries],
  );
  const medPassRows = useMemo(
    () => activeNonPrnMedicationsScheduledToday.map((medication: any) => {
      const medicationId = String(medication?.medicationId || "").trim();
      const occurrences = getMedicationScheduleOccurrences(medication, todayDate).map((occurrence: any) => {
        const scheduledTimeKey = String(occurrence?.scheduledTimeKey || "default").trim().toLowerCase();
        const entry = scheduledTodayEntries.find((item: any) => getEmarEntryOccurrenceKey(item) === `${medicationId}::${scheduledTimeKey}`) || null;
        return {
          entry,
          entryStatus: String(entry?.status || "").trim().toLowerCase(),
          scheduledTimeKey,
          occurrenceLabel: occurrence?.scheduledLabel || "",
          timingMeta: getTodayMedicationTimingMeta({
            schedule: occurrence?.scheduleText || medication?.schedule || "",
            scheduledClockTime: occurrence?.scheduledClockTime || "",
            prn: false,
          }, !!entry),
        };
      });
      return { medication, medicationId, occurrences };
    }),
    [activeNonPrnMedicationsScheduledToday, scheduledTodayEntries, todayDate],
  );
  const todayPassOccurrenceRows = useMemo(
    () => medPassRows.flatMap((row: any) => row.occurrences.map((occurrence: any) => ({
      medication: row.medication,
      medicationId: row.medicationId,
      occurrences: [occurrence],
    }))),
    [medPassRows],
  );
  const todayTrackingOccurrenceRows = useMemo(
    () => dailyTrackingRows.flatMap((row: any) => row.occurrences.map((occurrence: any) => ({
      item: row.item,
      trackingItemId: row.trackingItemId,
      occurrences: [occurrence],
    }))),
    [dailyTrackingRows],
  );
  const filteredTodayPassOccurrenceRows = useMemo(
    () => filterEmarPassRows(todayPassOccurrenceRows, todayPassFilter),
    [todayPassOccurrenceRows, todayPassFilter],
  );
  const filteredTodayTrackingOccurrenceRows = useMemo(
    () => filterEmarPassRows(todayTrackingOccurrenceRows, todayPassFilter),
    [todayTrackingOccurrenceRows, todayPassFilter],
  );
  const todayPassTimeSections = useMemo(() => {
    const nowTime = Date.now();
    const upcoming: any[] = [];
    const past: any[] = [];
    const notScheduled: any[] = [];
    const combinedRows = [
      ...filteredTodayPassOccurrenceRows.map((row: any) => ({ ...row, timeViewType: "medication" })),
      ...filteredTodayTrackingOccurrenceRows.map((row: any) => ({ ...row, timeViewType: "tracking" })),
    ];

    combinedRows.forEach((row: any) => {
      const occurrence = row?.occurrences?.[0];
      const scheduledTime = occurrence?.timingMeta?.scheduledDate instanceof Date
        ? occurrence.timingMeta.scheduledDate.getTime()
        : null;
      if (scheduledTime === null) {
        notScheduled.push(row);
        return;
      }
      if (scheduledTime !== null && scheduledTime > nowTime) {
        upcoming.push(row);
        return;
      }
      past.push(row);
    });

    const getOccurrenceSortTime = (row: any, fallback: number) => (
      row?.occurrences?.[0]?.timingMeta?.scheduledDate instanceof Date
        ? row.occurrences[0].timingMeta.scheduledDate.getTime()
        : fallback
    );

    upcoming.sort((first: any, second: any) => getOccurrenceSortTime(first, Number.MAX_SAFE_INTEGER) - getOccurrenceSortTime(second, Number.MAX_SAFE_INTEGER));
    past.sort((first: any, second: any) => getOccurrenceSortTime(second, 0) - getOccurrenceSortTime(first, 0));
    notScheduled.sort((first: any, second: any) => {
      const firstLabel = String(first?.item?.name || first?.medication?.medicationName || "").trim();
      const secondLabel = String(second?.item?.name || second?.medication?.medicationName || "").trim();
      return firstLabel.localeCompare(secondLabel);
    });
    return { upcoming, past, notScheduled };
  }, [filteredTodayPassOccurrenceRows, filteredTodayTrackingOccurrenceRows]);
  const isSavingDailyTracking = Boolean(savingDailyTrackingKey);

  const loadPage = async () => {
    if (!residentId) {
      return;
    }
    setLoading(true);
    try {
      const residentResponse = await ensureCaregiverResidentOperationalAccess(
        backendAPIStore,
        residentId,
      );
      setResidentAccess(residentResponse || null);
      setAccessMessage("");
      setShowSubscriptionDialog(false);
      const [medicationResponse, entryResponse, dailyTrackingItemResponse, dailyTrackingEntryResponse] = await Promise.all([
        backendAPIStore.getResidentEmarMedications(residentId),
        backendAPIStore.getResidentEmarEntries(residentId, { entryDate: todayDate, limit: 300 }),
        backendAPIStore.getResidentEmarDailyTrackingItems(residentId).catch(() => ({ items: [] })),
        backendAPIStore.getResidentEmarDailyTrackingEntries(residentId, { entryDate: todayDate, limit: 300 }).catch(() => ({ items: [] })),
      ]);
      setMedications(Array.isArray(medicationResponse?.items) ? medicationResponse.items : []);
      setTodayEntries(filterEntriesForTodayPass(entryResponse?.items, todayDate));
      setDailyTrackingItems(Array.isArray(dailyTrackingItemResponse?.items) ? dailyTrackingItemResponse.items : []);
      setTodayTrackingEntries(filterEntriesForTodayPass(dailyTrackingEntryResponse?.items, todayDate));
    } catch (error: any) {
      if (error?.caregiverAccessError) {
        setAccessMessage(error?.message || "eMAR is not available right now.");
        setMedications([]);
        setTodayEntries([]);
        setDailyTrackingItems([]);
        setTodayTrackingEntries([]);
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setShowSubscriptionDialog(true);
        }
        return;
      }
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to load eMAR.",
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPage();
  }, [residentId]);

  const generateMarReport = async () => {
    const homeId = String(residentAccess?.homeId || "").trim();
    if (!residentId || !homeId) {
      toast.current?.show({
        severity: "warn",
        summary: "Unable to generate report",
        detail: "Resident home details are missing.",
        life: 3000,
      });
      return;
    }

    try {
      setGeneratingReport(true);
      const chartStartDate = reportRangeMode === "monthly"
        ? getMonthStartDateOnly(reportMonthSelection)
        : String(reportStartDateSelection || "").trim();
      const chartEndDate = reportRangeMode === "monthly"
        ? getLosAngelesDateOnly(new Date(reportMonthSelection.getFullYear(), reportMonthSelection.getMonth() + 1, 0))
        : String(reportEndDateSelection || "").trim();
      const response = await backendAPIStore.generateResidentEmarMarReportDocument({
        residentId,
        homeId,
        chartStartDate,
        chartEndDate,
        blankReport: blankMarReport,
      });
      if (!response?.documentUrl) {
        throw new Error("The MAR report is missing a download link.");
      }
      window.open(response.documentUrl, "_blank", "noopener,noreferrer");
      toast.current?.show({
        severity: "success",
        summary: "Report ready",
        detail: response?.fileName || "The MAR report is ready to download.",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to generate the MAR report.",
        life: 4000,
      });
    } finally {
      setGeneratingReport(false);
    }
  };

  const showGenerateReportOptions = () => {
    setReportRangeMode("monthly");
    setReportMonthSelection(new Date());
    setReportStartDateSelection(monthStartDate);
    setReportEndDateSelection(todayDate);
    setReportExactDateSelection([new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date()]);
    setBlankMarReport(false);
    setShowGenerateReportDialog(true);
  };

  useEffect(() => {
    const handleGenerateReport = () => {
      showGenerateReportOptions();
    };

    window.addEventListener("resident-emar:request-generate-report", handleGenerateReport as EventListener);
    return () => {
      window.removeEventListener("resident-emar:request-generate-report", handleGenerateReport as EventListener);
    };
  }, [residentId, residentAccess?.homeId, monthStartDate, todayDate]);

  if (userRoleType && !canUseCaregiverWorkspace) {
    return <Unauthorized />;
  }

  const saveEntry = async (payload: any) => {
    if (!residentId) {
      return;
    }
    const matchingMedication = activeMedications.find((item: any) => String(item?.medicationId || "").trim() === String(payload?.medicationId || "").trim());
    if (matchingMedication?.effectiveEndDate && isMedicationExpired(matchingMedication.effectiveEndDate)) {
      toast.current?.show({
        severity: "warn",
        summary: "Medication expired",
        detail: `This medication expired on ${matchingMedication.effectiveEndDate}. Update the medication before charting another med pass.`,
        life: 4000,
      });
      return;
    }
    const nextSavingKey = `${String(payload?.medicationId || "").trim()}::${String(payload?.scheduledTimeKey || (String(payload?.status || "").toLowerCase() === "prn_given" ? "prn" : "default")).trim().toLowerCase()}`;
    setSavingEntryKey(nextSavingKey);
    setSavingEntry(true);
    try {
      const latestAccess = await ensureCaregiverResidentOperationalAccess(
        backendAPIStore,
        residentId,
      );
      setResidentAccess((previousValue: any) => ({ ...(previousValue || {}), ...latestAccess }));
      setAccessMessage("");
      const savedEntry = await backendAPIStore.upsertResidentEmarEntry({
        residentId,
        entryDate: todayDate,
        ...payload,
      });
      setTodayEntries((previousEntries: any[]) => {
        const nextOccurrenceKey = getEmarEntryOccurrenceKey(savedEntry || payload);
        const normalizedEntryId = String(savedEntry?.entryId || "").trim();
        const savedIsPrnEntry = String(savedEntry?.status || payload?.status || "").trim().toLowerCase() === "prn_given";
        const filteredEntries = previousEntries.filter((item: any) => {
          if (normalizedEntryId && String(item?.entryId || "").trim() === normalizedEntryId) {
            return false;
          }
          if (savedIsPrnEntry) {
            return true;
          }
          return getEmarEntryOccurrenceKey(item) !== nextOccurrenceKey;
        });
        return [savedEntry, ...filteredEntries].sort((first: any, second: any) => {
          const firstTime = new Date(first?.recordedAt || first?.modifiedAt || first?.createdAt || 0).getTime();
          const secondTime = new Date(second?.recordedAt || second?.modifiedAt || second?.createdAt || 0).getTime();
          return secondTime - firstTime;
        });
      });
    } catch (error: any) {
      if (error?.caregiverAccessError) {
        setAccessMessage(error?.message || "eMAR is not available right now.");
        if (error?.code === "SUBSCRIPTION_REQUIRED") {
          setShowSubscriptionDialog(true);
        }
      }
      if (error?.statusCode === 409) {
        await loadPage();
      }
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save medication entry.",
        life: 4000,
      });
    } finally {
      setSavingEntry(false);
      setSavingEntryKey("");
    }
  };

  const resolveProtocol = async (payload: any) => {
    if (!residentId) {
      return null;
    }
    return backendAPIStore.resolveResidentEmarProtocol({
      residentId,
      entryDate: todayDate,
      ...payload,
    });
  };

  const closeDailyTrackingDialog = () => {
    setShowDailyTrackingDialog(false);
    setPendingDailyTrackingAction(null);
    setDailyTrackingValueDraft({});
    setDailyTrackingNoteDraft("");
  };

  const openDailyTrackingDialog = (row: any, occurrence: any, status: "recorded" | "refused" | "family" | "hospitalized") => {
    if (["recorded", "refused"].includes(String(status || "").trim().toLowerCase()) && !canChartScheduledOccurrenceNow(occurrence)) {
      return;
    }
    const entry = occurrence?.entry || null;
    const entryDate = todayDate;
    const scheduledClockTime = String(occurrence?.timingMeta?.scheduledClockTime || row?.item?.scheduledClockTime || "").trim();
    const existingRecordedAt = String(entry?.recordedAt || entry?.modifiedAt || entry?.createdAt || "").trim();
    const nextTimeState = isDailyTrackingValueEntryStatus(status)
      ? buildDailyTrackingTimeSelectionState(entryDate, scheduledClockTime, existingRecordedAt || new Date().toISOString(), true)
      : null;
    const defaultRecordedAt = existingRecordedAt || (nextTimeState
      ? (nextTimeState.givenTimeChoice === "scheduled" ? nextTimeState.scheduledRecordedAt : nextTimeState.currentRecordedAt)
      : "");
    const initialGivenTimeChoice = !nextTimeState
      ? ""
      : defaultRecordedAt === nextTimeState.scheduledRecordedAt
        ? "scheduled"
        : defaultRecordedAt === nextTimeState.currentRecordedAt
          ? "current"
          : "custom";
    setPendingDailyTrackingAction({
      item: row?.item,
      trackingItemId: row?.trackingItemId,
      scheduledTimeKey: occurrence?.scheduledTimeKey || "default",
      scheduledTime: occurrence?.occurrenceLabel || occurrence?.timingMeta?.scheduledLabel || "Scheduled",
      scheduledClockTime,
      entryDate,
      recordedAt: defaultRecordedAt,
      givenTimeChoice: initialGivenTimeChoice,
      showGivenTimeChoiceDialog: nextTimeState?.showGivenTimeChoiceDialog || false,
      givenTimePromptReason: nextTimeState?.givenTimePromptReason || "",
      givenTimeOptions: nextTimeState?.givenTimeOptions || [],
      status,
      expectedVersion: Number(entry?.version || 0),
    });
    setDailyTrackingValueDraft(entry?.values && typeof entry.values === "object" ? entry.values : {});
    setDailyTrackingNoteDraft(String(entry?.note || ""));
    setShowDailyTrackingDialog(true);
  };

  const saveDailyTrackingEntry = async () => {
    if (!residentId || !pendingDailyTrackingAction?.trackingItemId) {
      return;
    }
    const status = normalizeValueKey(pendingDailyTrackingAction?.status);
    const schema = getEmarDailyTrackingSchema(pendingDailyTrackingAction?.item);
    if (isDailyTrackingValueEntryStatus(status)) {
      const missingField = schema.find((field: any) => field?.required && !String(dailyTrackingValueDraft[String(field?.key || "").trim()] || "").trim());
      if (missingField) {
        return;
      }
    } else if (!dailyTrackingNoteDraft.trim()) {
      return;
    }

    const savingKey = `${String(pendingDailyTrackingAction?.trackingItemId || "").trim()}::${String(pendingDailyTrackingAction?.scheduledTimeKey || "default").trim().toLowerCase()}`;
    setSavingDailyTrackingKey(savingKey);
    try {
      await ensureCaregiverResidentOperationalAccess(
        backendAPIStore,
        residentId,
      );
      const savedEntry = await backendAPIStore.upsertResidentEmarDailyTrackingEntry({
        residentId,
        entryDate: todayDate,
        trackingItemId: pendingDailyTrackingAction.trackingItemId,
        status,
        scheduledTimeKey: pendingDailyTrackingAction.scheduledTimeKey,
        scheduledTime: pendingDailyTrackingAction.scheduledTime || "",
        recordedAt: String(pendingDailyTrackingAction?.recordedAt || "").trim(),
        values: isDailyTrackingValueEntryStatus(status) ? dailyTrackingValueDraft : {},
        note: dailyTrackingNoteDraft.trim(),
        expectedVersion: pendingDailyTrackingAction.expectedVersion || 0,
      });
      setTodayTrackingEntries((previousEntries: any[]) => {
        const nextOccurrenceKey = getDailyTrackingOccurrenceKey(savedEntry);
        const normalizedEntryId = String(savedEntry?.entryId || "").trim();
        const filteredEntries = previousEntries.filter((item: any) => {
          if (normalizedEntryId && String(item?.entryId || "").trim() === normalizedEntryId) {
            return false;
          }
          return getDailyTrackingOccurrenceKey(item) !== nextOccurrenceKey;
        });
        return [savedEntry, ...filteredEntries].sort((first: any, second: any) => {
          const firstTime = new Date(first?.recordedAt || first?.modifiedAt || first?.createdAt || 0).getTime();
          const secondTime = new Date(second?.recordedAt || second?.modifiedAt || second?.createdAt || 0).getTime();
          return secondTime - firstTime;
        });
      });
      closeDailyTrackingDialog();
      if (getTrackingRecommendedActionLabel(savedEntry)) {
        setTrackingRecommendedActionDialog({
          title: getTrackingRecommendedActionLabel(savedEntry),
          message: getTrackingRecommendedActionMessage(savedEntry),
        });
      }
      toast.current?.show({
        severity: "success",
        summary: "Tracking saved",
        detail: getTrackingRecommendedActionLabel(savedEntry)
          ? getTrackingRecommendedActionMessage(savedEntry)
          : "The daily tracking entry was saved.",
        life: getTrackingRecommendedActionLabel(savedEntry) ? 4500 : 2500,
      });
    } catch (error: any) {
      if (error?.statusCode === 409) {
        await loadPage();
      }
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to save daily tracking entry.",
        life: 4000,
      });
    } finally {
      setSavingDailyTrackingKey("");
    }
  };

  const medListDetailsBodyTemplate = (row: any) => {
    const protocolSummary = getEmarProtocolSummary(row);
    return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 700, color: "#0f172a" }}>{row?.medicationName || "Medication"}</div>
      {[row?.dosage, row?.route].filter(Boolean).length > 0 ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.88rem" }}>
        {[row?.dosage, row?.route].filter(Boolean).join(" • ") || "Add dose or route"}
        </div>
      ) : null}
      {buildMedicationScheduleText(row) ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
          <strong style={{ color: "#334155" }}>Schedule:</strong> {buildMedicationScheduleText(row)}
        </div>
      ) : null}
      {row?.indication ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
          <strong style={{ color: "#334155" }}>Reason/Indication:</strong> {row.indication}
        </div>
      ) : null}
      {row?.instructions ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
          <strong style={{ color: "#334155" }}>Instructions:</strong> {row.instructions}
        </div>
      ) : null}
      {row?.handlingNotes ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem", whiteSpace: "pre-wrap" }}>
          <strong style={{ color: "#334155" }}>Handling notes:</strong> {row.handlingNotes}
        </div>
      ) : null}
      {protocolSummary ? (
        <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
          <span style={{ color: "#334155", fontWeight: 700 }}>{`Protocol: ${protocolSummary.label}`}</span>
        </div>
      ) : null}
      {row?.prn ? (
        <div className="mt-2">
          <Tag value="PRN" severity="warning" rounded />
        </div>
      ) : null}
      {protocolSummary ? (
        <div className="mt-2">
          <Tag value={String(protocolSummary.label || "Protocol")} severity="info" rounded />
        </div>
      ) : null}
    </div>
    );
  };

  const dailyTrackingItemBodyTemplate = (row: any) => {
    const categoryOption = getEmarDailyTrackingCategoryOption(row?.item?.category);
    const itemName = String(row?.item?.name || "").trim();
    const schema = getEmarDailyTrackingSchema(row?.item);
    const protocolSummary = getEmarProtocolSummary(row?.item);

    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: "#0f172a" }}>{itemName || categoryOption?.label || "Tracking item"}</div>
        {(row?.item?.effectiveStartDate || row?.item?.effectiveEndDate) ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Dates:</strong>{" "}
            {[row?.item?.effectiveStartDate ? `Start ${formatEmarDateOnly(row.item.effectiveStartDate)}` : "", row?.item?.effectiveEndDate ? `End ${formatEmarDateOnly(row.item.effectiveEndDate)}` : ""].filter(Boolean).join(" • ")}
          </div>
        ) : null}
        {schema.length ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Records:</strong>{" "}
            {schema.map((field: any) => `${field?.label || field?.key}${field?.unit ? ` (${field.unit})` : ""}`).filter(Boolean).join(", ")}
          </div>
        ) : null}
        {row?.item?.instructions ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Instructions:</strong> {row.item.instructions}
          </div>
        ) : null}
        {protocolSummary ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
            <span><strong style={{ color: "#334155" }}>Action rules:</strong>{` ${protocolSummary.label}`}</span>
          </div>
        ) : null}
      </div>
    );
  };

  const dailyTrackingStatusBodyTemplate = (row: any) => (
    <div style={{ minWidth: 0 }}>
      {!row.occurrences.length ? (
        <Tag value="Not scheduled today" severity="secondary" rounded />
      ) : null}
      <div className="flex flex-column gap-2">
        {row.occurrences.map((occurrence: any) => {
          const status = String(occurrence?.entryStatus || "").trim().toLowerCase();
          const effectiveStatus = status || (occurrence?.timingMeta?.isOverdue ? "missed" : "");
          const summaryTag = buildOccurrenceSummaryTag(
            occurrence,
            effectiveStatus ? formatDailyTrackingStatusLabel(effectiveStatus) : "",
            effectiveStatus ? getDailyTrackingStatusSeverity(effectiveStatus) : "",
          );
          return (
            <div key={`${row.trackingItemId}-${occurrence.scheduledTimeKey}`} style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.65rem 0.75rem", background: "#fff" }}>
              <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
                {summaryTag ? <Tag value={summaryTag.label} severity={summaryTag.severity as any} rounded /> : null}
              </div>
              <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                {occurrence?.entry
                  ? buildEmarDailyTrackingValueSummary(occurrence.entry, row?.item) || occurrence?.entry?.note || "No recorded values"
                  : occurrence?.timingMeta?.isOverdue
                    ? "No charted entry was saved for this scheduled tracking time."
                    : "No entry saved yet for this scheduled tracking time."}
              </div>
              {getTrackingRecommendedActionLabel(occurrence?.entry) ? (
                <div className="mt-2">
                  <Tag value={getTrackingRecommendedActionLabel(occurrence?.entry)} severity="danger" rounded />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );

  const dailyTrackingActionsBodyTemplate = (row: any) => (
    <div className="flex flex-column gap-2" style={{ minWidth: "18rem" }}>
      {!row.occurrences.length ? (
        <div style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
          This tracking item is active but not scheduled for today.
        </div>
      ) : null}
        {row.occurrences.map((occurrence: any) => {
          const status = String(occurrence?.entryStatus || "").trim().toLowerCase();
          const isRecorded = status === "recorded";
          const savingKey = `${String(row?.trackingItemId || "").trim()}::${String(occurrence?.scheduledTimeKey || "default").trim().toLowerCase()}`;
          const isSaving = savingDailyTrackingKey === savingKey;
          const canChartScheduledNow = canChartScheduledOccurrenceNow(occurrence);
          const chartingAvailableAtLabel = formatChartUnlockTimeLabel(occurrence?.timingMeta?.scheduledDate);
          return (
            <div key={`${row.trackingItemId}-${occurrence.scheduledTimeKey}`} style={{ border: "1px solid #e2e8f0", borderRadius: "12px", padding: "0.65rem 0.75rem", background: "#fff" }}>
              <div className="mb-2" style={{ fontSize: "0.8rem", fontWeight: 700, color: "#475569" }}>
                {occurrence?.occurrenceLabel || occurrence?.timingMeta?.scheduledLabel || "Scheduled"}
              </div>
              <div className="flex gap-2 flex-wrap">
                {canChartScheduledNow ? (
                  <Button type="button" label="Record" size="small" rounded severity="success" outlined={status !== "recorded"} disabled={isSaving} onClick={() => openDailyTrackingDialog(row, occurrence, "recorded")} />
                ) : null}
                {!isRecorded ? (
                  <>
                    {canChartScheduledNow ? (
                      <Button type="button" label="Refused" size="small" rounded severity="danger" outlined={status !== "refused"} disabled={isSaving} onClick={() => openDailyTrackingDialog(row, occurrence, "refused")} />
                    ) : null}
                    <Button type="button" label="Family" size="small" rounded severity="success" outlined={status !== "family"} disabled={isSaving} onClick={() => openDailyTrackingDialog(row, occurrence, "family")} />
                    <Button type="button" label="Hospitalized" size="small" rounded severity="warning" outlined={status !== "hospitalized"} disabled={isSaving} onClick={() => openDailyTrackingDialog(row, occurrence, "hospitalized")} />
                  </>
                ) : null}
              </div>
              {!canChartScheduledNow ? (
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.78rem" }}>
                  Record and Refused unlock 1 hour before the scheduled tracking time
                  {chartingAvailableAtLabel ? ` (${chartingAvailableAtLabel}).` : "."}
                </div>
              ) : null}
            </div>
          );
        })}
    </div>
  );

  const renderDailyTrackingRecordSection = () => (
    <div style={{ ...appInnerCardStyle, padding: "1rem 1.1rem" }}>
      <div>
        <div style={{ fontSize: "0.92rem", fontWeight: 800, color: "#0f172a" }}>Daily Tracking Records</div>
        <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "42rem" }}>
          Track readings like blood pressure, glucose, oxygen, pulse, temperature, weight, pain, and bowel movement here.
        </div>
      </div>
      <div className="mt-3">
        {activeDailyTrackingItems.length === 0 ? (
          <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
            No daily tracking items are active right now.
          </div>
        ) : filteredDailyTrackingRows.length === 0 ? (
          <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
            No daily tracking passes match this filter.
          </div>
        ) : (
          <>
            <div className="block md:hidden">
              <div className="flex flex-column gap-3">
                {filteredDailyTrackingRows.map((row: any) => (
                  <div key={row?.trackingItemId || row?.item?.name} style={{ ...appInnerCardStyle, padding: "0.9rem 1rem" }}>
                    {dailyTrackingItemBodyTemplate(row)}
                    <div className="mt-3">{dailyTrackingStatusBodyTemplate(row)}</div>
                    <div className="mt-3">{dailyTrackingActionsBodyTemplate(row)}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden md:block">
              <DataTable value={filteredDailyTrackingRows} responsiveLayout="scroll" size="small" stripedRows>
                <Column header="Tracking Item" body={dailyTrackingItemBodyTemplate} style={{ minWidth: "18rem" }} />
                <Column header="Status" body={dailyTrackingStatusBodyTemplate} style={{ minWidth: "17rem" }} />
                <Column header="Actions" body={dailyTrackingActionsBodyTemplate} style={{ minWidth: "20rem" }} />
              </DataTable>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const getTimeViewDailyTrackingRows = (timeBucket: "upcoming" | "past" | "notScheduled") => (
    filteredDailyTrackingRows
      .map((row: any) => ({
        ...row,
        occurrences: row.occurrences.filter((occurrence: any) => {
          const scheduledDate = occurrence?.timingMeta?.scheduledDate;
          if (!(scheduledDate instanceof Date) || Number.isNaN(scheduledDate.getTime())) {
            return timeBucket === "notScheduled";
          }
          if (timeBucket === "upcoming") {
            return scheduledDate.getTime() > Date.now();
          }
          if (timeBucket === "past") {
            return scheduledDate.getTime() <= Date.now();
          }
          return false;
        }),
      }))
      .filter((row: any) => row.occurrences.length > 0)
  );

  const renderTodayPassTimeSection = (title: string, timeBucket: "upcoming" | "past" | "notScheduled", hasRows: boolean) => (
    <div>
      <div style={{ fontSize: "0.96rem", fontWeight: 800, color: "#0f172a" }}>{title}</div>
      <div className="mt-3">
        {!hasRows ? (
          <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "0.75rem 0.5rem" }}>
            No items in this section right now.
          </div>
        ) : (
          <div className="flex flex-column gap-4">
            <ResidentEmarTodayPanel
              medications={activeNonPrnMedicationsScheduledToday}
              todayEntries={scheduledTodayEntries}
              savingEntry={savingEntry}
              savingEntryKey={savingEntryKey}
              onSaveEntry={saveEntry}
              onResolveProtocol={resolveProtocol}
              layoutMode="occurrences"
              compactActions
              useActionDialog
              timeBucket={timeBucket}
              passFilter={todayPassFilter}
              emptyMessage="No medication passes match this filter."
              todayDate={todayDate}
            />
            {getTimeViewDailyTrackingRows(timeBucket).length > 0 ? (
              <div>
                <div className="block md:hidden">
                  <div className="flex flex-column gap-3">
                    {getTimeViewDailyTrackingRows(timeBucket).map((row: any) => (
                      <div key={row?.trackingItemId || row?.item?.name} style={{ padding: "0.1rem 0" }}>
                        {dailyTrackingItemBodyTemplate(row)}
                        <div className="mt-3">{dailyTrackingStatusBodyTemplate(row)}</div>
                        <div className="mt-3">{dailyTrackingActionsBodyTemplate(row)}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="hidden md:block">
                  <DataTable
                    value={getTimeViewDailyTrackingRows(timeBucket)}
                    responsiveLayout="scroll"
                    size="small"
                    stripedRows
                  >
                    <Column header="Tracking Item" body={dailyTrackingItemBodyTemplate} style={{ minWidth: "18rem" }} />
                    <Column header="Status" body={dailyTrackingStatusBodyTemplate} style={{ minWidth: "17rem" }} />
                    <Column header="Actions" body={dailyTrackingActionsBodyTemplate} style={{ minWidth: "20rem" }} />
                  </DataTable>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="container-fluid px-3 py-3 caregiver-resident-emar-page p-component">
      <Toast ref={toast} />
      {generatingReport ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255, 255, 255, 0.72)",
            backdropFilter: "blur(1px)",
            zIndex: 1300,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.9rem",
          }}
        >
          <ProgressSpinner style={{ width: "3.2rem", height: "3.2rem" }} />
          <div style={{ color: "#0f172a", fontWeight: 700 }}>Generating MAR report...</div>
        </div>
      ) : null}
      <ProviderSubscriptionDialog
        visible={showSubscriptionDialog}
        featureName="eMAR"
        message="eMAR is unavailable because the provider subscription is inactive."
        showBillingButton={false}
        onHide={() => setShowSubscriptionDialog(false)}
      />
      <Dialog
        visible={showGenerateReportDialog}
        style={{ width: "32rem", maxWidth: "96vw" }}
        modal
        onHide={() => {
          if (!generatingReport) {
            setShowGenerateReportDialog(false);
          }
        }}
        header={renderSurfaceHeader(
          "Generate eMAR Report",
          "Choose either a monthly report or an exact date range.",
          "pi pi-calendar",
        )}
      >
        <div className="pt-2 flex flex-column gap-3">
          <div style={{ ...appNeutralPanelStyle, padding: "1rem" }}>
            <div className="flex align-items-center gap-2 flex-wrap mb-3">
              <Button
                type="button"
                label="Monthly"
                rounded
                outlined
                severity="secondary"
                style={{
                  ...reportRangeButtonStyle,
                  background: reportRangeMode === "monthly" ? "#f1f5f9" : "#fff",
                  color: "#0f172a",
                }}
                onClick={() => setReportRangeMode("monthly")}
              />
              <Button
                type="button"
                label="Exact dates"
                rounded
                outlined
                severity="secondary"
                style={{
                  ...reportRangeButtonStyle,
                  background: reportRangeMode === "exact_dates" ? "#f1f5f9" : "#fff",
                  color: "#0f172a",
                }}
                onClick={() => setReportRangeMode("exact_dates")}
              />
            </div>
            {reportRangeMode === "monthly" ? (
              <>
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.5rem" }}>Month</div>
                <Calendar
                  value={reportMonthSelection}
                  onChange={(event) => setReportMonthSelection(event.value instanceof Date ? event.value : new Date())}
                  view="month"
                  dateFormat="M yy"
                  maxDate={new Date()}
                  showButtonBar
                  yearNavigator
                  yearRange="2020:2035"
                  showIcon
                  style={{ width: "100%" }}
                />
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                  The monthly report generates the full selected month.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.5rem" }}>Exact date range</div>
                <Calendar
                  value={reportExactDateSelection as Date[]}
                  onChange={(event) => {
                    const nextValue = Array.isArray(event.value) ? event.value as (Date | null)[] : [];
                    setReportExactDateSelection(nextValue);
                    const nextStart = nextValue[0] instanceof Date ? getLosAngelesDateOnly(nextValue[0]) : "";
                    const nextEnd = nextValue[1] instanceof Date ? getLosAngelesDateOnly(nextValue[1]) : "";
                    setReportStartDateSelection(nextStart);
                    setReportEndDateSelection(nextEnd || nextStart);
                  }}
                  selectionMode="range"
                  readOnlyInput
                  hideOnRangeSelection
                  maxDate={new Date()}
                  showIcon
                  style={{ width: "100%" }}
                />
                <div className="mt-2 flex gap-2 flex-wrap align-items-center">
                  <Tag
                    value={`${getInclusiveDateRangeDayCount(reportStartDateSelection, reportEndDateSelection)} day range`}
                    severity={getInclusiveDateRangeDayCount(reportStartDateSelection, reportEndDateSelection) > 31 ? "danger" : "info"}
                    rounded
                  />
                  {getInclusiveDateRangeDayCount(reportStartDateSelection, reportEndDateSelection) > 31 ? <Tag value="Max 31 days" severity="danger" rounded /> : null}
                </div>
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                  Choose any exact date range up to 31 days total.
                </div>
              </>
            )}
            <div className="mt-3">
              <label style={{ display: "flex", alignItems: "center", gap: "0.7rem", cursor: "pointer" }}>
                <Checkbox
                  inputId="caregiver-emar-blank-report-checkbox"
                  checked={blankMarReport}
                  onChange={(event) => setBlankMarReport(Boolean(event.checked))}
                />
                <span style={{ fontWeight: 700, color: "#0f172a" }}>Generate blank MAR report</span>
              </label>
              <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                Keep medications, schedules, resident details, and contacts the same, but leave charting and exceptions blank.
              </div>
            </div>
          </div>
          <div className="flex justify-content-end gap-2 flex-wrap">
            <Button type="button" label="Cancel" severity="secondary" outlined rounded onClick={() => setShowGenerateReportDialog(false)} disabled={generatingReport} />
            <Button
              type="button"
              label="Download"
              severity="warning"
              rounded
              loading={generatingReport}
              disabled={reportRangeMode === "exact_dates" && (getInclusiveDateRangeDayCount(reportStartDateSelection, reportEndDateSelection) < 1 || getInclusiveDateRangeDayCount(reportStartDateSelection, reportEndDateSelection) > 31)}
              onClick={async () => {
                await generateMarReport();
                setShowGenerateReportDialog(false);
              }}
            />
          </div>
        </div>
      </Dialog>
      <style>
        {`
          .caregiver-resident-emar-page .resident-workspace-tabs .p-tabview-nav-container {
            margin-bottom: 1rem;
          }

          .caregiver-resident-emar-page .resident-workspace-tabs {
            width: 100%;
          }

          .caregiver-resident-emar-page .resident-workspace-tabs .p-tabview-nav {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 0.55rem;
            padding: 0.45rem;
            border: 1px solid #dbe4f0;
            border-radius: 18px;
            background: linear-gradient(180deg, #f8fbff 0%, #f1f5f9 100%);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
            width: 100%;
          }

          .caregiver-resident-emar-page .resident-workspace-tabs .p-tabview-nav li {
            margin: 0;
          }

          .caregiver-resident-emar-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link {
            border: 1px solid #d7e0ea;
            border-radius: 14px;
            background: #ffffff;
            color: #475569;
            padding: 0.8rem 1rem;
            font-weight: 700;
            transition: all 0.18s ease;
            box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
          }

          .caregiver-resident-emar-page .resident-workspace-tabs .p-tabview-nav li .p-tabview-nav-link:not(.p-disabled):focus {
            box-shadow: 0 0 0 0.18rem rgba(59, 130, 246, 0.16);
          }

          .caregiver-resident-emar-page .resident-workspace-tabs .p-tabview-nav li:not(.p-highlight) .p-tabview-nav-link:hover {
            border-color: #bfdbfe;
            color: #1d4ed8;
            transform: translateY(-1px);
          }

          .caregiver-resident-emar-page .resident-workspace-tabs .p-tabview-nav li.p-highlight .p-tabview-nav-link {
            border-color: #fdba74;
            background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 55%, #fef3c7 100%);
            color: #0f172a;
            box-shadow: 0 8px 18px rgba(249, 115, 22, 0.14);
          }

          .caregiver-resident-emar-page .resident-workspace-tabs .p-tabview-panels {
            padding: 0;
            background: transparent;
          }

          .caregiver-resident-emar-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel {
            display: flex;
            flex-direction: column;
            align-items: stretch;
          }

          .caregiver-resident-emar-page .resident-workspace-tabs .p-tabview-panels .p-tabview-panel > * {
            width: 100%;
          }

        `}
      </style>
      <div style={{ width: "100%" }}>
        <div className="flex flex-wrap justify-content-between align-items-start gap-3">
          <div style={{ minWidth: 0 }}>
            {renderSurfaceHeader(
              `${residentAccess?.residentName || residentDisplayName} eMAR`,
              "Manage meds and chart today's doses.",
              "pi pi-table",
            )}
          </div>
          <div className="flex align-items-center gap-2 flex-wrap" style={{ justifyContent: "flex-end" }}>
            {sharedMode ? (
              <Button
                type="button"
                label="Generate Report"
                icon="pi pi-file-export"
                severity="warning"
                rounded
                outlined
                loading={generatingReport}
                disabled={loading || Boolean(accessMessage)}
                onClick={showGenerateReportOptions}
              />
            ) : null}
          {(todayPassSummary.missedScheduled + todayPassSummary.missedEntries + todayPassSummary.missedTracking > 0
            || todayPassSummary.expiringSoon > 0
            || todayPassSummary.expired > 0) ? (
            <div className="flex gap-2 flex-wrap align-items-center" style={{ justifyContent: "flex-end", maxWidth: "36rem" }}>
              <span style={{ ...appMutedTextStyle, fontWeight: 700 }}>Today&apos;s Status:</span>
              {todayPassSummary.missedScheduled + todayPassSummary.missedEntries + todayPassSummary.missedTracking > 0 ? (
                <Tag value={`${todayPassSummary.missedScheduled + todayPassSummary.missedEntries + todayPassSummary.missedTracking} missed`} severity="danger" rounded />
              ) : null}
              {todayPassSummary.expiringSoon > 0 ? (
                <Tag value={`${todayPassSummary.expiringSoon} expiring soon`} severity="warning" rounded />
              ) : null}
              {todayPassSummary.expired > 0 ? (
                <Tag value={`${todayPassSummary.expired} expired`} severity="danger" rounded />
              ) : null}
            </div>
          ) : null}
          </div>
        </div>
        {loading ? (
          <div className="flex justify-content-center py-6">
            <ProgressSpinner style={{ width: "3rem", height: "3rem" }} />
          </div>
        ) : accessMessage ? (
          <div className="mt-4" style={{ ...appInnerCardStyle, textAlign: "center" }}>
            <div style={{ fontWeight: 800, color: "#0f172a" }}>eMAR unavailable</div>
            <div className="mt-2" style={appMutedTextStyle}>
              {accessMessage}
            </div>
          </div>
        ) : activeMedications.length === 0 ? (
          <div className="mt-4" style={{ ...appInnerCardStyle, textAlign: "center", background: "#f8fafc" }}>
            <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Current Medications</div>
            <div className="mt-2" style={{ fontSize: "0.96rem", fontWeight: 700, color: "#0f172a" }}>No active medications yet</div>
            <div className="mt-2" style={{ ...appMutedTextStyle, maxWidth: "40rem", margin: "0 auto" }}>
              The provider still needs to set up the resident medication list before daily eMAR charting can start.
            </div>
          </div>
        ) : (
          <div className="mt-4">
            {medicationExpiryAlerts.length > 0 ? (
              <div
                style={{
                  ...appInnerCardStyle,
                  maxWidth: "52rem",
                  margin: "0 auto 1rem auto",
                  padding: "1rem 1.1rem",
                  border: "1px solid #f59e0b",
                  background: "linear-gradient(180deg, #fff7ed 0%, #fffaf3 100%)",
                  textAlign: "center",
                }}
              >
                <div className="flex justify-content-between align-items-center gap-3 flex-wrap">
                  <div style={{ minWidth: 0, flex: "1 1 22rem", textAlign: "left" }}>
                    <div style={{ fontSize: "1rem", fontWeight: 800, color: "#9a3412" }}>Medication Expiration Alerts</div>
                    <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "36rem" }}>
                      {showMedicationExpiryAlertsExpanded
                        ? "Review these active medications before charting today's meds."
                        : `${medicationExpiryAlerts.slice(0, 3).map((alert) => alert.medicationName).join(", ")}${medicationExpiryAlerts.length > 3 ? ` +${medicationExpiryAlerts.length - 3} more` : ""}`}
                    </div>
                  </div>
                  <Button
                    type="button"
                    label={showMedicationExpiryAlertsExpanded ? "Minimize" : "Show list"}
                    icon={showMedicationExpiryAlertsExpanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                    size="small"
                    rounded
                    outlined
                    severity="warning"
                    onClick={() => setShowMedicationExpiryAlertsExpanded((current) => !current)}
                  />
                </div>
                {showMedicationExpiryAlertsExpanded ? (
                  <div className="mt-3 flex flex-column gap-2">
                    {medicationExpiryAlerts.map((alert) => (
                      <div
                        key={`${alert.medicationId}-${alert.effectiveEndDate}`}
                        className="flex justify-content-between align-items-center gap-3 flex-wrap"
                        style={{
                          background: "#ffffff",
                          border: `1px solid ${alert.status === "expired" ? "#fecaca" : "#fed7aa"}`,
                          borderRadius: 14,
                          padding: "0.8rem 0.95rem",
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        <div style={{ minWidth: 0, flex: "1 1 18rem" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>{alert.medicationName}</div>
                          <div className="mt-1" style={appMutedTextStyle}>
                            {alert.status === "expired" ? "Expired on" : "Expires on"} {formatEmarDateOnly(alert.effectiveEndDate)}
                          </div>
                        </div>
                        <Tag value={alert.status === "expired" ? "Expired" : "Expiring soon"} severity={alert.status === "expired" ? "danger" : "warning"} rounded />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            <TabView className="resident-workspace-tabs" scrollable>
              <TabPanel header={`Today's Meds (${activeNonPrnMedicationsScheduledToday.length + dailyTrackingRows.length})`}>
                <div style={{ padding: "0.25rem 0" }}>
                  <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Today&apos;s Meds</div>
                      <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                        Review scheduled meds for today and mark what happened.
                      </div>
                    </div>
                    <div className="flex align-items-center gap-2 flex-wrap" style={{ marginLeft: "auto" }}>
                      <span style={{ ...appMutedTextStyle, fontWeight: 700 }}>Filter</span>
                      <Dropdown
                        value={todayPassFilter}
                        options={[
                          { label: "All passes", value: "all" },
                          { label: "Pending", value: "pending" },
                          { label: "Charted", value: "charted" },
                        ]}
                        optionLabel="label"
                        optionValue="value"
                        onChange={(event) => setTodayPassFilter(event.value as "all" | "pending" | "charted")}
                        style={{ minWidth: "12rem" }}
                      />
                      <span style={{ ...appMutedTextStyle, fontWeight: 700 }}>View by</span>
                      <Button
                        type="button"
                        label="Medication"
                        size="small"
                        rounded
                        severity={todayPassViewMode === "medication" ? "warning" : "secondary"}
                        outlined={todayPassViewMode !== "medication"}
                        onClick={() => setTodayPassViewMode("medication")}
                      />
                      <Button
                        type="button"
                        label="Time"
                        size="small"
                        rounded
                        severity={todayPassViewMode === "time" ? "warning" : "secondary"}
                        outlined={todayPassViewMode !== "time"}
                        onClick={() => setTodayPassViewMode("time")}
                      />
                    </div>
                  </div>
                  {todayPassViewMode === "medication" ? (
                    <>
                      <div className="mt-3">
                        {activeNonPrnMedicationsScheduledToday.length === 0 ? (
                          <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
                            No scheduled meds are due today.
                          </div>
                        ) : (
                          <ResidentEmarTodayPanel
                            medications={activeNonPrnMedicationsScheduledToday}
                            todayEntries={scheduledTodayEntries}
                            savingEntry={savingEntry}
                            savingEntryKey={savingEntryKey}
                            onSaveEntry={saveEntry}
                            onResolveProtocol={resolveProtocol}
                            layoutMode="all"
                            compactActions
                            useActionDialog
                            passFilter={todayPassFilter}
                            emptyMessage="No medication passes match this filter."
                            todayDate={todayDate}
                          />
                        )}
                      </div>
                      <div className="mt-4">
                        {renderDailyTrackingRecordSection()}
                      </div>
                    </>
                  ) : (
                    <div className="mt-3 flex flex-column gap-3">
                      {activeNonPrnMedicationsScheduledToday.length === 0 && dailyTrackingRows.length === 0 ? (
                        <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
                          No scheduled meds or daily tracking items are due right now.
                        </div>
                      ) : (
                        <>
                          {renderTodayPassTimeSection("Upcoming", "upcoming", todayPassTimeSections.upcoming.length > 0)}
                          {renderTodayPassTimeSection("Past", "past", todayPassTimeSections.past.length > 0)}
                          {renderTodayPassTimeSection("Not scheduled", "notScheduled", todayPassTimeSections.notScheduled.length > 0)}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </TabPanel>
              <TabPanel header={`PRN Meds (${activePrnMedications.length})`}>
                <div style={{ padding: "0.25rem 0" }}>
                  <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>PRN Meds</div>
                  <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                    Review PRN meds here and chart them separately from scheduled meds.
                  </div>
                  <div className="mt-3">
                    {activePrnMedications.length === 0 ? (
                      <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
                        No PRN meds are active right now.
                      </div>
                    ) : (
                      <ResidentEmarTodayPanel
                        residentId={residentId || ""}
                        medications={activePrnMedications}
                        todayEntries={prnTodayEntries}
                        savingEntry={savingEntry}
                        savingEntryKey={savingEntryKey}
                        onSaveEntry={saveEntry}
                        onResolveProtocol={resolveProtocol}
                        layoutMode="all"
                        compactActions
                        useActionDialog
                      />
                    )}
                  </div>
                </div>
              </TabPanel>
              <TabPanel header={`Med List (${activeMedications.length})`}>
                <div style={{ padding: "0.25rem 0" }}>
                  <div style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Med List</div>
                  <div className="mt-1" style={{ ...appMutedTextStyle, maxWidth: "40rem" }}>
                    View the active medication list with schedule, directions, and PRN details.
                  </div>
                  <div className="mt-3">
                    <div className="block md:hidden flex flex-column gap-2">
                      {activeMedications.map((medication: any) => (
                        <div
                          key={String(medication?.medicationId || medication?.residentEmarMedicationId || medication?.id || medication?.medicationName || "medication")}
                          style={{ ...appInnerCardStyle, padding: "0.9rem", overflowWrap: "anywhere" }}
                        >
                          {medListDetailsBodyTemplate(medication)}
                        </div>
                      ))}
                    </div>
                    <div className="hidden md:block">
                      <DataTable value={activeMedications} responsiveLayout="scroll" size="small" stripedRows>
                        <Column header="Medication Details" body={medListDetailsBodyTemplate} style={{ minWidth: "24rem" }} />
                        <Column
                          header="Summary"
                          body={(row: any) => (
                            <div style={{ minWidth: 0 }}>
                              <div style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                                {getMedicationScheduleDisplay(row) || "Add dose, route, or schedule in setup"}
                              </div>
                            </div>
                          )}
                          style={{ minWidth: "18rem" }}
                        />
                      </DataTable>
                    </div>
                  </div>
                </div>
              </TabPanel>
            </TabView>
          </div>
        )}
      </div>
      <Dialog
        visible={!!trackingRecommendedActionDialog}
        header="Recommended Action"
        style={{ width: "min(92vw, 30rem)" }}
        onHide={() => setTrackingRecommendedActionDialog(null)}
        footer={(
          <div className="flex justify-content-end">
            <Button type="button" label="Close" severity="warning" onClick={() => setTrackingRecommendedActionDialog(null)} />
          </div>
        )}
      >
        <div
          style={{
            border: "1px solid #fed7aa",
            background: "#fff7ed",
            borderRadius: "16px",
            padding: "1rem",
          }}
        >
          <div style={{ fontWeight: 800, color: "#9a3412", marginBottom: "0.5rem" }}>
            {trackingRecommendedActionDialog?.title || "Recommended Action"}
          </div>
          <div style={{ color: "#7c2d12", lineHeight: 1.55 }}>
            {trackingRecommendedActionDialog?.message || ""}
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={showDailyTrackingDialog}
        header={pendingDailyTrackingAction ? `${pendingDailyTrackingAction?.item?.name || "Daily Tracking"} • ${pendingDailyTrackingAction?.scheduledTime || "Scheduled"}` : "Daily Tracking"}
        style={{ width: "52rem", maxWidth: "96vw" }}
        modal
        onHide={closeDailyTrackingDialog}
        footer={(
          <div className="flex justify-content-end gap-2">
            <Button type="button" label="Cancel" outlined disabled={isSavingDailyTracking} onClick={closeDailyTrackingDialog} />
            <Button type="button" label="Save" severity="warning" loading={isSavingDailyTracking} disabled={isSavingDailyTracking} onClick={() => void saveDailyTrackingEntry()} />
          </div>
        )}
      >
        {pendingDailyTrackingAction ? (
          <div className="flex flex-column gap-3">
            <div className="flex align-items-center gap-2 flex-wrap">
              <Tag value={formatDailyTrackingStatusLabel(pendingDailyTrackingAction?.status)} severity={getDailyTrackingStatusSeverity(pendingDailyTrackingAction?.status) as any} rounded />
              <div style={{ ...appMutedTextStyle, fontSize: "0.84rem" }}>
                {buildMedicationScheduleText(pendingDailyTrackingAction?.item) || pendingDailyTrackingAction?.scheduledTime || "Scheduled"}
              </div>
            </div>
            {isDailyTrackingValueEntryStatus(pendingDailyTrackingAction?.status) ? (
              <div className="emar-record-action-grid">
              {getEmarDailyTrackingSchema(pendingDailyTrackingAction?.item).map((field: any) => {
                const fieldKey = String(field?.key || "").trim();
                return (
                  <div key={fieldKey}>
                    <label style={{ display: "block", fontSize: "0.84rem", fontWeight: 700, color: "#334155", marginBottom: "0.35rem" }}>
                      {field?.label || fieldKey}{field?.unit ? ` (${field.unit})` : ""}{field?.required ? " *" : ""}
                    </label>
                    <InputText
                      value={dailyTrackingValueDraft[fieldKey] || ""}
                      onChange={(event) => setDailyTrackingValueDraft((previousValue) => ({ ...previousValue, [fieldKey]: event.target.value }))}
                      placeholder={getEmarDailyTrackingFieldPlaceholder(field)}
                      style={{ width: "100%" }}
                      inputMode={field?.inputType === "number" ? "decimal" : undefined}
                    />
                  </div>
                );
              })}
              </div>
            ) : null}
            {isDailyTrackingValueEntryStatus(pendingDailyTrackingAction?.status) && pendingDailyTrackingAction?.showGivenTimeChoiceDialog ? (
              <div style={{ border: "1px solid #dbeafe", background: "#f8fbff", borderRadius: "16px", padding: "0.85rem 0.95rem", marginBottom: "1rem" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Choose administration time</div>
                <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                  Choose whether to save it at the scheduled time, the current time, or an exact charting time.
                </div>
                {pendingDailyTrackingAction?.givenTimePromptReason ? (
                  <div className="mt-2" style={{ border: "1px solid #fde68a", background: "#fffbeb", borderRadius: "12px", padding: "0.8rem 0.9rem", color: "#92400e", fontSize: "0.88rem", lineHeight: 1.5 }}>
                    {pendingDailyTrackingAction.givenTimePromptReason}
                  </div>
                ) : null}
                <div className="mt-2 grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 15rem), 15rem))", width: "100%", maxWidth: "36rem", justifyContent: "center", margin: "0.5rem auto 0", gap: "0.5rem" }}>
                  {(pendingDailyTrackingAction?.givenTimeOptions || []).map((option: any) => {
                    const isSelected = String(pendingDailyTrackingAction?.givenTimeChoice || "") === String(option?.value || "");
                    return (
                      <div className="col-12 md:col-6" key={String(option?.value || "")} style={{ width: "100%", padding: 0 }}>
                        <button
                          type="button"
                          disabled={option?.disabled}
                          onClick={() => setPendingDailyTrackingAction((current: any) => ({
                            ...current,
                            givenTimeChoice: option?.value,
                            recordedAt: option?.recordedAt || current?.recordedAt || "",
                          }))}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            border: isSelected ? "1px solid #2563eb" : "1px solid #dbeafe",
                            borderRadius: 12,
                            padding: "0.7rem 0.8rem",
                            background: isSelected ? "#eff6ff" : "#fff",
                            cursor: option?.disabled ? "not-allowed" : "pointer",
                            opacity: option?.disabled ? 0.6 : 1,
                          }}
                        >
                          <div style={{ fontWeight: 700 }}>{option?.label || "Time option"}</div>
                          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                            {option?.helper || ""}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                  {pendingDailyTrackingAction?.givenTimeChoice === "custom" ? (
                    <div className="col-12 md:col-6" style={{ width: "100%", padding: 0 }}>
                      <div style={{ minHeight: "5.25rem", border: "1px solid #dbe3ef", borderRadius: "14px", padding: "0.8rem 0.9rem", background: "#fff" }}>
                        <label className="block mb-2 font-medium">Exact charting time</label>
                        <Dropdown
                          value={String(getLocalTimeValueWithSecondsFromIso(pendingDailyTrackingAction?.recordedAt) || "").slice(0, 5)}
                          options={getAvailableChartTimeOptions(pendingDailyTrackingAction?.entryDate)}
                          onChange={(event) => {
                            const nextTimeValue = String(event.value || "");
                            setPendingDailyTrackingAction((current: any) => ({
                              ...current,
                              recordedAt: updateIsoDateTimeParts(current?.recordedAt, current?.entryDate, nextTimeValue),
                            }));
                          }}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Select time"
                          disabled={pendingDailyTrackingAction?.givenTimeChoice !== "custom"}
                          style={{ width: "100%" }}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div>
              <label style={{ display: "block", fontSize: "0.84rem", fontWeight: 700, color: "#334155", marginBottom: "0.35rem" }}>
                {isDailyTrackingValueEntryStatus(pendingDailyTrackingAction?.status) ? "Note" : "Reason / Note *"}
              </label>
              <InputTextarea
                value={dailyTrackingNoteDraft}
                onChange={(event) => setDailyTrackingNoteDraft(event.target.value)}
                rows={4}
                autoResize
                style={{ width: "100%" }}
                placeholder={isDailyTrackingValueEntryStatus(pendingDailyTrackingAction?.status) ? "Optional note" : "Add a short note"}
              />
            </div>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
};

const CaregiverResidentEmarPage = () => <CaregiverResidentEmarPageContent />;

export default CaregiverResidentEmarPage;
